// React wrapper around attachExcelGrid.
//
// It returns a *callback ref*: pass the result as `ref={attach}` on the grid
// container. The callback fires when the node actually mounts (and again with
// null on unmount), so it works even when the grid is rendered late — e.g. the
// live-preview surface returns null until its transaction model loads, so a
// mount-effect keyed on a RefObject would run once against a null container and
// never re-attach. The callback ref sidesteps that entirely.
//
// Host callbacks (writeCell / readCell / ensureRowCount) are read through a ref
// so passing fresh closures every render doesn't tear the listeners down.

import { useCallback, useRef } from 'react'
import { attachExcelGrid, type ExcelGridOptions } from './excelGrid'

export type ExcelGridRef = (node: HTMLElement | null) => void

export function useExcelGrid(options: ExcelGridOptions): ExcelGridRef {
  const optsRef = useRef(options)
  optsRef.current = options
  const detachRef = useRef<() => void>(() => {})

  const enabled = options.enabled !== false
  const basicNav = !!options.basicNav
  const fillHandle = options.fillHandle !== false

  // Identity changes only when a structural flag flips; React then re-invokes
  // the ref (old(null) → new(node)) which detaches and re-attaches.
  return useCallback((node: HTMLElement | null) => {
    detachRef.current()
    detachRef.current = () => {}
    if (!node || !enabled) return
    detachRef.current = attachExcelGrid(node, {
      enabled: true,
      basicNav,
      fillHandle,
      writeCell: (row, col, text) => optsRef.current.writeCell(row, col, text),
      readCell: (row, col) => optsRef.current.readCell?.(row, col) ?? null,
      ensureRowCount: optsRef.current.ensureRowCount
        ? (count: number) => optsRef.current.ensureRowCount!(count)
        : undefined,
    })
  }, [enabled, basicNav, fillHandle])
}

export { attachExcelGrid } from './excelGrid'
export type { ExcelGridOptions, ExcelCellAddr } from './excelGrid'
